import React, { useCallback, useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Typography, Image } from 'lib/components';
import NurseRow from './NurseRow';
import { Checkbox } from 'lib/components/FormFields';
import { useWindowResize } from 'hooks/useWindowResize';
import { Nurse } from 'types';
import { HeaderItem, Sort, SortField } from './types';
import { nurseEmailCompareFn, nurseNameCompareFn, nurseExperienceCompareFn } from './sort';
import classes from './index.module.scss';

interface Props {
	nurses: Nurse[];
	checkedId: number;
	checkAction: (id: number, isChecked: boolean) => void;
}

const HEADER_ITEMS: HeaderItem[] = [
	{ label: 'Avatar' },
	{ label: 'Name', field: 'name', sort: 'asc' },
	{ label: 'Email', field: 'email', sort: 'asc' },
	{ label: 'Phone' },
	{ label: 'Employee Type' },
	{ label: 'Years of Experience', field: 'experience', sort: 'asc' },
	{ label: 'Registered' },
];

const NursesDataTable = ({ nurses, checkedId, checkAction }: Props) => {
	const [allChecked, setAllChecked] = useState(false);
	const { isSmallerDevice } = useWindowResize();
	const [sortFields] = useState(
		HEADER_ITEMS.filter((item) => item.field && item.sort).reduce<Record<string, Sort>>((acc, cur) => {
			if (cur.field && cur.sort) {
				acc[cur.field] = cur!.sort;
			}
			return acc;
		}, {})
	);
	const [currentSortField, setCurrentSortField] = useState<SortField>({
		sort: 'asc',
		field: 'name',
	});
	const [sortedNurses, setSortedNurses] = useState<Nurse[]>(nurses);

	const sortNurses = useCallback(
		(nurses: Nurse[]) => {
			const sorted = [...nurses].sort((a, b) => {
				switch (currentSortField?.sort) {
					case 'asc':
						if (currentSortField?.field === 'name') {
							return nurseNameCompareFn(a, b, 'asc');
						} else if (currentSortField?.field === 'email') {
							return nurseEmailCompareFn(a, b, 'asc');
						} else if (currentSortField?.field === 'experience') {
							return nurseExperienceCompareFn(a, b, 'asc');
						}
						return 0;

					case 'desc':
						if (currentSortField?.field === 'name') {
							return nurseNameCompareFn(a, b, 'desc');
						} else if (currentSortField?.field === 'email') {
							return nurseEmailCompareFn(a, b, 'desc');
						} else if (currentSortField?.field === 'experience') {
							return nurseExperienceCompareFn(a, b, 'desc');
						}
						return 0;

					default:
						return 0;
				}
			});
			return sorted;
		},
		[currentSortField]
	);

	useEffect(() => {
		setSortedNurses(sortNurses(nurses));
	}, [nurses, sortNurses]);

	useEffect(() => {
		const sorted = sortNurses(sortedNurses);

		setSortedNurses(sorted);
	}, [sortNurses, setSortedNurses]);

	const handleHeaderItemClick = useCallback(
		(headerItem: HeaderItem) => {
			if (headerItem.field && sortFields[headerItem.field]) {
				if (headerItem.field === currentSortField?.field) {
					setCurrentSortField((prev) => {
						return {
							...prev,
							sort: prev?.sort === 'asc' ? 'desc' : 'asc',
						};
					});
				} else if (currentSortField?.field !== headerItem.field) {
					setCurrentSortField({ field: headerItem.field, sort: 'desc' });
				}
			}
		},
		[currentSortField, sortFields, setCurrentSortField]
	);

	const renderHeaderItem = (headerItem: HeaderItem) => {
		if (!headerItem.field || !headerItem.sort) {
			return (
				<div key={Math.random()} className={classes['col']}>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					{/* <Image src={'icTableDown'} /> */}
				</div>
			);
		} else {
			return (
				<div
					onClick={() => handleHeaderItemClick(headerItem)}
					key={Math.random()}
					className={classes['col']}
					style={{
						cursor: 'pointer',
					}}
				>
					<Typography weight="light" variant={isSmallerDevice ? 'h5' : 'h6'}>
						{headerItem.label}
					</Typography>
					<Image
						src={'icTableDown'}
						style={{
							marginLeft: '0.5rem',
							transform:
								currentSortField?.field === headerItem.field && currentSortField.sort === 'desc'
									? 'rotate(180deg)'
									: 'initial',
						}}
					/>
				</div>
			);
		}
	};

	return (
		<div className={classes['inventories-datatable']}>
			<div className={classes['table-wrapper']}>
				<div className={classes['table-container']}>
					<div className={classes['toolbar']}>
						<Checkbox
							checked={allChecked}
							onChange={() => setAllChecked(!allChecked)}
							style={{
								margin: '0 2rem 0.1rem 2rem',
							}}
						/>
						{HEADER_ITEMS.map((item) => renderHeaderItem(item))}
					</div>
					<div className={classes['container']}>
						<Scrollbars
							style={{
								flex: 1,
							}}
						>
							{sortedNurses.map((nurse) => (
								<NurseRow
									key={nurse.id}
									nurse={nurse}
									checkedId={checkedId}
									checkAction={checkAction}
								/>
							))}
						</Scrollbars>
					</div>
				</div>
			</div>
		</div>
	);
};

export default NursesDataTable;
